import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  ClipboardList,
  ExternalLink,
  Loader2,
  Search,
  User,
} from 'lucide-react';
import { fmt } from './posV2Utils';
import {
  COMMANDES_STEPS,
  buildCommandeCartLine,
  buildCommandePayload,
  defaultAcompteForCommande,
  filterActiveCommandes,
} from './posV2CommandesUtils';

const PosV2CommandesDrawer = ({ api, sessionMag, onAddLine, onClose }) => {
  const [step, setStep] = useState(0);
  const [searchQ, setSearchQ] = useState('');
  const [cmdSearchQ, setCmdSearchQ] = useState('');
  const [activeCommandes, setActiveCommandes] = useState([]);
  const [loadingCommandes, setLoadingCommandes] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [demande, setDemande] = useState({
    designation: '',
    marque: '',
    modele: '',
    prix: '',
    notes: '',
    ref_piece: '',
    fournisseur: '',
  });
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [client, setClient] = useState({ nom: '', prenom: '', telephone: '' });
  const [commande, setCommande] = useState(null);
  const [acompte, setAcompte] = useState('50');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const stepId = COMMANDES_STEPS[step]?.id;

  const loadActiveCommandes = useCallback(async () => {
    setLoadingCommandes(true);
    try {
      const res = await api.get('/commandes');
      setActiveCommandes(res.data || []);
    } catch {
      setActiveCommandes([]);
    } finally {
      setLoadingCommandes(false);
    }
  }, [api]);

  useEffect(() => {
    loadActiveCommandes();
  }, [loadActiveCommandes]);

  const filteredCommandes = useMemo(
    () => filterActiveCommandes(activeCommandes, cmdSearchQ),
    [activeCommandes, cmdSearchQ],
  );

  const resumeCommande = (cmd) => {
    setCommande(cmd);
    setClient({ nom: cmd.client_nom || '', prenom: '', telephone: cmd.client_tel || '' });
    setDemande((d) => ({ ...d, designation: cmd.piece || '', prix: String(cmd.prix_final || '') }));
    setAcompte(defaultAcompteForCommande(cmd));
    setError('');
    setStep(Number(cmd.reste_a_payer || 0) > 0 ? 3 : 4);
  };

  const runSearch = useCallback(async () => {
    if (searchQ.trim().length < 2) return;
    setSearching(true);
    try {
      const res = await api.get(`/stock/search/${encodeURIComponent(searchQ.trim())}`, {
        params: { magasin_id: sessionMag },
      });
      setSearchResults(res.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [api, searchQ, sessionMag]);

  const searchClients = async (q) => {
    if (q.trim().length < 2) {
      setClientResults([]);
      return;
    }
    try {
      const res = await api.get(`/clients/autocomplete/${encodeURIComponent(q.trim())}`);
      setClientResults(res.data || []);
    } catch {
      setClientResults([]);
    }
  };

  const pickProduct = (item) => {
    setDemande((d) => ({
      ...d,
      designation: item.designation || item.reference || '',
      marque: item.marque || '',
      modele: item.modele || '',
      prix: String(item.prix_effectif || item.prix_vente || item.prix || ''),
      ref_piece: item.reference || '',
    }));
    setStep(1);
  };

  const createCommande = async () => {
    setError('');
    setLoading(true);
    try {
      let clientTel = client.telephone.trim();
      let clientNom = [client.prenom, client.nom].filter(Boolean).join(' ').trim() || client.nom.trim();
      if (!clientTel && clientNom) {
        const created = await api.post('/clients', {
          nom: clientNom,
          telephone: '0000000000',
          email: '',
        });
        clientTel = created.data?.telephone || '';
        clientNom = created.data?.nom || clientNom;
      }
      const payload = buildCommandePayload(demande, { nom: clientNom, telephone: clientTel }, sessionMag);
      if (!payload) {
        setError('Désignation, client et prix requis');
        return;
      }
      const res = await api.post('/commandes', payload);
      setCommande(res.data);
      setAcompte(defaultAcompteForCommande(res.data));
      await loadActiveCommandes();
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur création commande');
    } finally {
      setLoading(false);
    }
  };

  const encaisserAcompte = () => {
    const line = buildCommandeCartLine(commande, acompte);
    if (!line) {
      setError('Montant acompte invalide');
      return;
    }
    onAddLine(line);
    setStep(4);
  };

  const recap = useMemo(() => ({
    produit: demande.designation || '—',
    marqueModele: [demande.marque, demande.modele].filter(Boolean).join(' / ') || '—',
    prix: demande.prix ? `${fmt(demande.prix)} €` : '—',
    client: client.nom ? [client.prenom, client.nom].filter(Boolean).join(' ') : '—',
    acompte: commande ? `${fmt(acompte)} €` : '0,00 €',
    statut: commande ? (step >= 4 ? 'ACOMPTE PANIER' : 'COMMANDE CRÉÉE') : 'EN ATTENTE',
  }), [demande, client, acompte, commande, step]);

  return (
    <div className="space-y-4 flex flex-col min-h-[60vh]">
      <p className="text-sm text-gray-400 flex items-start gap-2">
        <ClipboardList size={18} className="text-purple-400 shrink-0 mt-0.5" />
        <span>
          Demande client → commande → acompte en caisse
          <span className="block text-[10px] text-gray-500 mt-1">Session {sessionMag}</span>
        </span>
      </p>

      <div className="flex flex-wrap gap-1">
        {COMMANDES_STEPS.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            onClick={() => idx <= step && setStep(idx)}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold ${
              idx === step ? 'bg-purple-600 text-white' : idx < step ? 'bg-white/10 text-gray-200 border-white/20' : 'bg-black/30 text-gray-700 border-white/10'
            }`}
          >
            {idx < step ? <Check size={10} /> : idx + 1}
            {s.label}
          </button>
        ))}
      </div>

      {error && (
        <p className="text-xs text-red-300 bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>
      )}

      {stepId === 'search' && (
        <div className="space-y-3 flex-1">
          <div className="rounded-xl border border-purple-500/30 bg-purple-950/20 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-bold uppercase tracking-wide text-purple-300">
                Commandes en cours
              </h3>
              <a
                href="/commandes"
                className="text-[10px] text-purple-200/80 hover:text-white flex items-center gap-1"
              >
                Toutes <ExternalLink size={10} />
              </a>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
              <input
                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-white text-sm"
                placeholder="Ref, client, pièce…"
                value={cmdSearchQ}
                onChange={(e) => setCmdSearchQ(e.target.value)}
              />
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {loadingCommandes && (
                <p className="text-xs text-gray-500 py-3 text-center flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Chargement…
                </p>
              )}
              {!loadingCommandes && filteredCommandes.map((cmd) => (
                <button
                  key={cmd.id}
                  type="button"
                  onClick={() => resumeCommande(cmd)}
                  className="w-full text-left p-2.5 rounded-lg border border-white/10 bg-black/20 text-gray-200 hover:border-purple-500/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-mono text-purple-200">{cmd.ref_commande}</div>
                      <div className="text-sm font-medium truncate">{cmd.piece}</div>
                      <div className="text-[10px] text-gray-500 truncate">{cmd.client_nom}</div>
                    </div>
                    <div className="text-right shrink-0 text-[10px]">
                      <div className="text-emerald-300">{fmt(cmd.total_acomptes || 0)} € versé</div>
                      <div className="text-gray-400">reste {fmt(cmd.reste_a_payer ?? cmd.prix_final)} €</div>
                    </div>
                  </div>
                </button>
              ))}
              {!loadingCommandes && !filteredCommandes.length && (
                <p className="text-xs text-gray-500 py-3 text-center">Aucune commande active</p>
              )}
            </div>
          </div>

          <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Nouvelle commande</p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
              <input
                className="w-full pl-9 pr-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white"
                placeholder="Nom, marque, réf…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              />
            </div>
            <button type="button" onClick={runSearch} className="px-4 py-3 rounded-lg bg-red-600 font-semibold">
              {searching ? <Loader2 size={16} className="animate-spin" /> : 'Chercher'}
            </button>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {searchResults.map((item) => (
              <button
                key={item.id || item.reference}
                type="button"
                onClick={() => pickProduct(item)}
                className="w-full text-left p-3 rounded-lg border border-white/10 bg-black/20 text-gray-200 hover:border-purple-500/40"
              >
                <div className="font-semibold text-sm">{item.designation}</div>
                <div className="text-xs text-gray-500">{item.reference} · stock {item.quantite ?? 0}</div>
              </button>
            ))}
            {!searchResults.length && !searching && (
              <p className="text-center text-gray-500 text-sm py-6">Aucun produit — créez une demande manuelle</p>
            )}
          </div>
          <button type="button" onClick={() => setStep(1)} className="w-full py-3 rounded-lg border border-dashed border-purple-500/40 text-purple-300 font-semibold">
            + Créer une demande
          </button>
        </div>
      )}

      {stepId === 'demande' && (
        <div className="space-y-3 flex-1">
          <input className="w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white" placeholder="Désignation *"
            value={demande.designation} onChange={(e) => setDemande((d) => ({ ...d, designation: e.target.value }))} />
          <div className="grid grid-cols-2 gap-2">
            <input className="px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white" placeholder="Marque"
              value={demande.marque} onChange={(e) => setDemande((d) => ({ ...d, marque: e.target.value }))} />
            <input className="px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white" placeholder="Modèle"
              value={demande.modele} onChange={(e) => setDemande((d) => ({ ...d, modele: e.target.value }))} />
          </div>
          <input type="number" min="0" step="0.01" className="w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white" placeholder="Prix estimé TTC (€) *"
            value={demande.prix} onChange={(e) => setDemande((d) => ({ ...d, prix: e.target.value }))} />
          <textarea className="w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white min-h-[72px]" placeholder="Notes (optionnel)"
            value={demande.notes} onChange={(e) => setDemande((d) => ({ ...d, notes: e.target.value }))} />
          <button type="button" onClick={() => setStep(2)} disabled={!demande.designation.trim() || !demande.prix}
            className="w-full min-h-[48px] rounded-xl bg-purple-600 font-bold disabled:opacity-40">
            Continuer · Client
          </button>
        </div>
      )}

      {stepId === 'client' && (
        <div className="space-y-3 flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <input className="w-full pl-9 pr-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white" placeholder="Rechercher client…"
              value={clientSearch} onChange={(e) => { setClientSearch(e.target.value); searchClients(e.target.value); }} />
          </div>
          {clientResults.length > 0 && (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {clientResults.map((c) => (
                <button key={c.id} type="button" onClick={() => {
                  setClient({ nom: c.nom || '', prenom: c.prenom || '', telephone: c.telephone || '' });
                  setClientResults([]);
                }} className="w-full text-left p-2 rounded-lg bg-black/20 border border-white/10 text-gray-200 text-sm">
                  {c.nom} · {c.telephone}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 flex items-center gap-1"><User size={12} /> Créer rapidement</p>
          <div className="grid grid-cols-2 gap-2">
            <input className="px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white" placeholder="Nom *"
              value={client.nom} onChange={(e) => setClient((c) => ({ ...c, nom: e.target.value }))} />
            <input className="px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white" placeholder="Prénom"
              value={client.prenom} onChange={(e) => setClient((c) => ({ ...c, prenom: e.target.value }))} />
          </div>
          <input className="w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white" placeholder="Téléphone"
            value={client.telephone} onChange={(e) => setClient((c) => ({ ...c, telephone: e.target.value }))} />
          <button type="button" onClick={createCommande} disabled={loading || !client.nom.trim()}
            className="w-full min-h-[48px] rounded-xl bg-purple-600 font-bold disabled:opacity-40">
            {loading ? <Loader2 className="animate-spin mx-auto" size={20} /> : 'Enregistrer la commande'}
          </button>
        </div>
      )}

      {stepId === 'acompte' && commande && (
        <div className="space-y-4 flex-1">
          <div className="p-3 rounded-lg bg-black/30 border border-white/10 text-white text-sm">
            <div className="font-semibold">{commande.ref_commande || commande.id?.slice(0, 8)}</div>
            <div className="text-gray-400">{commande.piece}</div>
            <div className="text-red-400 font-bold mt-1">{fmt(commande.prix_final)} €</div>
            {(commande.total_acomptes > 0 || commande.reste_a_payer != null) && (
              <div className="text-[10px] text-gray-500 mt-2 space-y-0.5">
                <div>Acomptes versés : {fmt(commande.total_acomptes || 0)} €</div>
                <div>Reste à payer : {fmt(commande.reste_a_payer ?? commande.prix_final)} €</div>
              </div>
            )}
          </div>
          {Number(commande.reste_a_payer ?? commande.prix_final) <= 0 ? (
            <p className="text-sm text-emerald-300 text-center py-4">Commande entièrement réglée.</p>
          ) : (
            <>
          <label className="block text-xs text-gray-400">
            Montant acompte (€)
            <input type="number" min="10" step="0.01" className="mt-1 w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white text-lg font-bold"
              value={acompte} onChange={(e) => setAcompte(e.target.value)} />
          </label>
          <input type="range" min="10" max={Math.min(500, commande.reste_a_payer ?? commande.prix_final ?? 500)} step="5"
            className="w-full" value={Math.min(parseFloat(acompte) || 10, commande.reste_a_payer ?? commande.prix_final ?? 500)}
            onChange={(e) => setAcompte(e.target.value)} />
          <button type="button" onClick={encaisserAcompte} className="w-full min-h-[56px] rounded-xl bg-red-600 font-bold">
            Encaisser l&apos;acompte {fmt(acompte)} €
          </button>
            </>
          )}
          <button type="button" onClick={() => setStep(0)} className="w-full py-2.5 rounded-lg border border-white/10 text-sm text-gray-400">
            ← Retour aux commandes en cours
          </button>
        </div>
      )}

      {stepId === 'commande' && commande && (
        <div className="space-y-4 flex-1 text-center py-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-900/40 flex items-center justify-center">
            <Check className="text-emerald-400" size={32} />
          </div>
          <h3 className="text-lg font-bold">Commande enregistrée</h3>
          <p className="text-sm text-gray-400">
            {commande.ref_commande} · acompte ajouté au panier — encaissez depuis la colonne droite.
          </p>
          <button type="button" onClick={onClose} className="w-full min-h-[48px] rounded-xl bg-white/10 font-semibold">
            Fermer et encaisser
          </button>
        </div>
      )}

      <div className="mt-auto pt-3 border-t border-white/10 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px]">
        <div><span className="text-gray-500">Produit</span><div className="truncate">{recap.produit}</div></div>
        <div><span className="text-gray-500">Client</span><div className="truncate">{recap.client}</div></div>
        <div><span className="text-gray-500">Acompte</span><div>{recap.acompte}</div></div>
        <div><span className="text-gray-500">Prix est.</span><div>{recap.prix}</div></div>
        <div className="col-span-2 sm:col-span-1"><span className="text-gray-500">Statut</span><div className="text-purple-300 font-semibold">{recap.statut}</div></div>
      </div>
    </div>
  );
};

export default PosV2CommandesDrawer;
